import {useCallback, useRef} from 'react'

export interface VideoRef {
  container: React.MutableRefObject<HTMLDivElement | null>
}

const useVideo = ({container}: VideoRef) => {
  const videoSrc = useRef<string>('')
  const currentVideoTime = useRef<number>(-1)

  const play = useCallback(() => {
    const video = container.current?.querySelector('video')
    if (video?.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
      try {
        video.play()
      } catch {
        // Ignore errors
      }
    }
  }, [container])

  const toggle = useCallback(
    (isPaused: boolean) => {
      const video = container.current?.querySelector('video')
      if (video?.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
        try {
          video[isPaused ? 'pause' : 'play']()
        } catch {
          // Ignore errors
        }
      }
    },
    [container],
  )

  const addListeners = useCallback(() => {
    const video = container.current?.querySelector('video')
    if (video) {
      video.addEventListener('canplay', play)
    }
  }, [play, container])

  const removeListeners = useCallback(() => {
    const video = container.current?.querySelector('video')
    if (video) {
      video.removeEventListener('canplay', play)
    }
  }, [play, container])

  const onVideoTimeUpdate = useCallback((event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const videoEl = event.currentTarget as HTMLVideoElement
    if (!videoEl) return

    // Resync the video if we just switched desktop/mobile assets
    if (videoSrc.current !== videoEl.src && currentVideoTime.current >= 0) {
      videoEl.currentTime = currentVideoTime.current
    }

    videoSrc.current = videoEl.src || ''
    currentVideoTime.current = videoEl.currentTime || 0
  }, [])

  return {
    toggle,
    addListeners,
    removeListeners,
    onVideoTimeUpdate,
  }
}

export default useVideo
